import { useRef, useState } from "react";
import emailjs from "emailjs-com";
import { FaGithub, FaLinkedinIn, FaInstagram, FaFacebookF } from "react-icons/fa";
import { Reveal, MaskText, Magnetic } from "./ui/motion";
import { Panel, Parallax } from "../journey/Panel";

const SOCIALS = [
  { icon: <FaGithub />, url: "https://github.com/ntsiakiris", label: "GitHub" },
  {
    icon: <FaLinkedinIn />,
    url: "https://www.linkedin.com/in/nikolaos-tsiakiris-b047a2229/",
    label: "LinkedIn",
  },
  { icon: <FaInstagram />, url: "https://www.instagram.com/tsak.__/", label: "Instagram" },
  { icon: <FaFacebookF />, url: "https://www.facebook.com/nicktsiakiris/", label: "Facebook" },
];

const FIELD =
  "w-full bg-transparent border-b border-paper/20 py-3 text-paper placeholder:text-paper-dim focus:outline-none focus:border-acid transition-colors duration-300";

function SectionHeader() {
  return (
    <div className="flex items-center gap-4 mb-12">
      <span className="eyebrow">(05)</span>
      <span className="h-px w-12 bg-acid" />
      <span className="eyebrow">Get In Touch</span>
    </div>
  );
}

function Blurb() {
  return (
    <p className="text-lg text-paper-dim leading-relaxed max-w-md">
      Got a project, a role you're hiring for, or just want to say hi?
      Send a message and I'll get back to you soon.
    </p>
  );
}

function SocialPills() {
  return (
    <div className="flex flex-col gap-4">
      <span className="eyebrow">Find me online</span>
      <div className="flex flex-wrap gap-3">
        {SOCIALS.map((s) => (
          <Magnetic key={s.label} strength={0.4}>
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-cursor-label={s.label.toLowerCase()}
              className="group flex items-center gap-3 rounded-full border border-paper/20 pl-4 pr-5 py-2.5 text-paper hover:bg-acid hover:text-ink hover:border-acid transition-colors duration-300"
            >
              <span className="text-base">{s.icon}</span>
              <span className="font-mono text-[11px] uppercase tracking-[0.15em]">
                {s.label}
              </span>
            </a>
          </Magnetic>
        ))}
      </div>
    </div>
  );
}

function ContactForm({ formRef, loading, status, onSubmit }) {
  return (
    <form ref={formRef} onSubmit={onSubmit} className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <label htmlFor="name" className="eyebrow">
          01 · Your Name
        </label>
        <input id="name" name="name" type="text" required placeholder="Jane Doe" className={FIELD} />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="email" className="eyebrow">
          02 · Email Address
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          placeholder="jane@email.com"
          className={FIELD}
        />
      </div>
      <div className="flex flex-col gap-2">
        <label htmlFor="message" className="eyebrow">
          03 · Message
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={4}
          placeholder="Tell me about your project…"
          className={`${FIELD} resize-none`}
        />
      </div>

      <div className="flex items-center gap-6 mt-2">
        <Magnetic strength={0.4}>
          <button
            type="submit"
            disabled={loading}
            data-cursor-label="send"
            className="group inline-flex items-center gap-3 rounded-full bg-acid text-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-paper transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? "Sending…" : "Send Message"}
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </button>
        </Magnetic>

        {status === "ok" && (
          <span className="text-sm text-acid">Message sent — thank you!</span>
        )}
        {status === "error" && (
          <span className="text-sm text-red-400">
            Something went wrong. Please try again.
          </span>
        )}
      </div>
    </form>
  );
}

export default function Contact({ horizontal }) {
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // null | "ok" | "error"

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await emailjs.sendForm(
        "service_dxnt43f",
        "template_oxyzlza",
        formRef.current,
        "I-JQ3QNYTWeGxbdAz"
      );
      formRef.current.reset();
      setStatus("ok");
    } catch (err) {
      console.error("Error:", err);
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  if (horizontal) {
    return (
      <section id="contact" className="flex h-full shrink-0">
        {/* Scene 1 — the invitation */}
        <Panel horizontal className="flex flex-col justify-center px-6 sm:px-10">
          <SectionHeader />
          <Parallax horizontal depth={-0.05}>
            <MaskText
              className="display text-[8vw] leading-[0.9] mb-14"
              lineClassName="text-paper"
              lines={[
                <>Let's build</>,
                <>
                  something <span className="italic text-acid">together.</span>
                </>,
              ]}
            />
          </Parallax>
          <div className="flex flex-col gap-10 max-w-xl">
            <Reveal>
              <Blurb />
            </Reveal>
            <Reveal delay={0.1}>
              <SocialPills />
            </Reveal>
          </div>
        </Panel>

        {/* Scene 2 — the form */}
        <Panel horizontal className="flex items-center px-10 xl:px-24">
          <div className="w-full max-w-2xl mx-auto">
            <Reveal>
              <ContactForm
                formRef={formRef}
                loading={loading}
                status={status}
                onSubmit={handleSubmit}
              />
            </Reveal>
          </div>
        </Panel>
      </section>
    );
  }

  return (
    <section id="contact" className="relative py-24 sm:py-36 px-6 sm:px-10">
      <SectionHeader />

      <MaskText
        className="display text-[14vw] mdl:text-[9vw] leading-[0.9] mb-20"
        lineClassName="text-paper"
        lines={[
          <>Let's build</>,
          <>
            something <span className="italic text-acid">together.</span>
          </>,
        ]}
      />

      <div className="grid lgl:grid-cols-[1fr_1.2fr] gap-16">
        {/* left — info */}
        <div className="flex flex-col gap-12">
          <Reveal>
            <Blurb />
          </Reveal>
          <Reveal delay={0.1}>
            <SocialPills />
          </Reveal>
        </div>

        {/* right — form */}
        <Reveal delay={0.15}>
          <ContactForm
            formRef={formRef}
            loading={loading}
            status={status}
            onSubmit={handleSubmit}
          />
        </Reveal>
      </div>
    </section>
  );
}
